import { useCallback, useEffect, useMemo, useState } from 'react';
import { entriesApi, type Entry } from './api.js';
import { AHEAD_DAYS, addDays, buildSeries, fromISODay, toISODay, type DayRow } from './stats.js';

/** Fenêtre minimale affichée, même pour un compte qui vient d'être créé. */
const MIN_DAYS = 180;

/** Minuit local du jour courant. */
function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export interface EntriesStore {
  /**
   * Historique dense jusqu'à aujourd'hui inclus. C'est la série des *calculs* :
   * moyennes, tendances, maintien. Demain n'y figure pas — une journée à peine
   * commencée tirerait toutes les moyennes vers le bas.
   */
  series: DayRow[];
  /**
   * La même série prolongée de `AHEAD_DAYS` jours, pour la *saisie* seulement.
   * Les pas, les calories et la séance d'une ligne sont ceux de la veille : le
   * soir venu, on les connaît déjà, et leur place est la journée de demain. Un
   * plan de repas, lui, se saisit d'un coup pour la quinzaine.
   */
  editable: DayRow[];
  loading: boolean;
  error: string | null;
  /** Index d'aujourd'hui, commun aux deux séries. */
  todayIndex: number;
  /** Jour ISO de demain, cible du geste du soir. */
  tomorrow: string;
  /** Dernière journée ouverte à la saisie. */
  maxDay: string;
  /** Position dans `editable`, donc `series.length` pour demain. */
  indexOf: (day: string) => number;
  save: (day: string, values: Omit<Entry, 'day'>) => Promise<void>;
  remove: (day: string) => Promise<void>;
  /** Relit l'état serveur : après un import, la série locale est périmée. */
  reload: () => Promise<void>;
}

/**
 * Charge l'historique complet en une fois. Un suivi quotidien produit au plus
 * quelques centaines de lignes par an : la pagination coûterait plus cher en
 * complexité qu'elle ne rapporterait, et tout est déjà en mémoire pour les
 * moyennes glissantes et le calendrier.
 */
export function useEntries(enabled: boolean): EntriesStore {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  // Figé entre deux rendus — la série ne doit pas glisser parce qu'un rendu
  // tombe après minuit — mais resynchronisé quand l'app revient au premier
  // plan. Une PWA mobile reste montée des jours entiers : sans cela, la série
  // s'arrête la veille et « Saisir aujourd'hui » désigne un jour absent de la
  // série, si bien que le panneau refuse silencieusement de s'ouvrir.
  const [todayDate, setTodayDate] = useState(startOfToday);

  useEffect(() => {
    const sync = () => {
      const now = startOfToday();
      setTodayDate((current) => (current.getTime() === now.getTime() ? current : now));
    };
    document.addEventListener('visibilitychange', sync);
    globalThis.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      globalThis.removeEventListener('focus', sync);
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setEntries([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const from = toISODay(addDays(todayDate, -(MIN_DAYS * 4)));
    void entriesApi
      .list(from, toISODay(addDays(todayDate, AHEAD_DAYS)))
      .then((result) => {
        if (cancelled) return;
        setEntries(result.entries);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof Error ? cause.message : 'Chargement impossible');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, todayDate]);

  // Construite jusqu'au dernier jour saisissable, puis tronquée : une seule
  // boucle, et les deux séries partagent forcément leurs index.
  const editable = useMemo(() => {
    const first = entries[0];
    const earliest = first ? fromISODay(first.day) : addDays(todayDate, -(MIN_DAYS - 1));
    const floor = addDays(todayDate, -(MIN_DAYS - 1));
    return buildSeries(
      earliest < floor ? earliest : floor,
      addDays(todayDate, AHEAD_DAYS),
      entries,
    );
  }, [entries, todayDate]);

  const series = useMemo(() => editable.slice(0, -AHEAD_DAYS), [editable]);

  const indexByDay = useMemo(() => {
    const map = new Map<string, number>();
    editable.forEach((row, index) => map.set(row.day, index));
    return map;
  }, [editable]);

  const indexOf = useCallback((day: string) => indexByDay.get(day) ?? -1, [indexByDay]);

  // Écriture optimiste : la saisie doit répondre au doigt, sans attendre le
  // réseau. En cas d'échec, l'état serveur est rechargé pour ne pas laisser
  // une valeur fantôme à l'écran.
  const applyLocally = useCallback((day: string, entry: Entry | null) => {
    setEntries((current) => {
      const without = current.filter((item) => item.day !== day);
      if (!entry) return without;
      return [...without, entry].sort((a, b) => a.day.localeCompare(b.day));
    });
  }, []);

  const reload = useCallback(async () => {
    const from = toISODay(addDays(todayDate, -(MIN_DAYS * 4)));
    const result = await entriesApi.list(from, toISODay(addDays(todayDate, AHEAD_DAYS)));
    setEntries(result.entries);
  }, [todayDate]);

  const save = useCallback(
    async (day: string, values: Omit<Entry, 'day'>) => {
      // Même règle qu'au serveur, `gym` compris : une séance seule suffit à
      // faire exister la journée, et la retirer ici la ferait clignoter.
      const isEmpty =
        values.weight == null && values.steps == null && values.kcal == null && !values.gym;
      applyLocally(day, isEmpty ? null : { day, ...values });
      try {
        const result = await entriesApi.save(day, values);
        applyLocally(day, result.entry);
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Enregistrement impossible');
        await reload().catch(() => undefined);
      }
    },
    [applyLocally, reload],
  );

  const remove = useCallback(
    async (day: string) => {
      applyLocally(day, null);
      try {
        await entriesApi.remove(day);
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Suppression impossible');
        await reload().catch(() => undefined);
      }
    },
    [applyLocally, reload],
  );

  return {
    series,
    editable,
    loading,
    error,
    todayIndex: series.length - 1,
    tomorrow: toISODay(addDays(todayDate, 1)),
    maxDay: toISODay(addDays(todayDate, AHEAD_DAYS)),
    indexOf,
    save,
    remove,
    reload,
  };
}
